/**
 * useQueueStatus — Dev-panel hook that polls the offline sync queue state.
 *
 * Returns aggregated state for the A6-30 dev panel:
 *   { depth, syncStatus, lastSyncTime, tierBreakdown, storageUsage,
 *     storageQuota, storagePct, online }
 *
 * Status heuristic (since syncEngine doesn't expose authoritative status):
 *   - navigator.onLine === false -> 'offline'
 *   - depth === 0 -> 'idle'
 *   - depth > 0 and depth decreasing across polls -> 'syncing'
 *   - depth > 0 and depth stable for 2+ polls -> 'stuck'
 *
 * Polling interval: 2000ms. Auto-cleanup on unmount.
 */

import { useEffect, useState, useRef } from 'react';
import { getDepth } from '../lib/offlineQueueDB';
import { getStorageEstimate } from '../lib/queueQuota';

const POLL_INTERVAL_MS = 2000;
const HISTORY_LENGTH = 3;

function deriveStatus(history, online) {
  if (!online) return 'offline';
  if (history.length === 0) return 'idle';
  const latest = history[history.length - 1];
  if (latest === 0) return 'idle';
  if (history.length < 2) return 'syncing';
  const prev = history[history.length - 2];
  if (latest < prev) return 'syncing';
  // Stable for 2+ polls -> stuck
  if (history.length >= 2 && history.slice(-2).every(d => d === latest)) return 'stuck';
  return 'syncing';
}

export function useQueueStatus({ pollInterval = POLL_INTERVAL_MS } = {}) {
  const [state, setState] = useState({
    depth: 0,
    syncStatus: 'idle',
    lastSyncTime: null,
    tierBreakdown: { tier1: 0, tier3: 0 },
    storageUsage: 0,
    storageQuota: 0,
    storagePct: 0,
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  });

  const historyRef = useRef([]);
  const lastDecreaseRef = useRef(null);

  useEffect(() => {
    let active = true;

    const poll = async () => {
      if (!active) return;
      try {
        const depth = await getDepth();
        const storage = await getStorageEstimate();
        const online = typeof navigator !== 'undefined' ? navigator.onLine : true;

        const history = [...historyRef.current, depth].slice(-HISTORY_LENGTH);
        historyRef.current = history;

        if (history.length >= 2 && history[history.length - 1] < history[history.length - 2]) {
          lastDecreaseRef.current = Date.now();
        }

        const syncStatus = deriveStatus(history, online);

        if (!active) return;
        setState({
          depth,
          syncStatus,
          lastSyncTime: lastDecreaseRef.current,
          tierBreakdown: { tier1: 0, tier3: 0 },
          storageUsage: storage?.usage ?? 0,
          storageQuota: storage?.quota ?? 0,
          storagePct: storage?.usagePct ?? 0,
          online,
        });
      } catch {
        // Polling errors are expected when DB not initialized
      }
    };

    poll();
    const id = setInterval(poll, pollInterval);

    const onOnline = () => {
      if (!active) return;
      setState(s => ({ ...s, online: true }));
    };
    const onOffline = () => {
      if (!active) return;
      setState(s => ({ ...s, online: false, syncStatus: 'offline' }));
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      active = false;
      clearInterval(id);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [pollInterval]);

  return state;
}

export default useQueueStatus;
